import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getPrivateInstance } from "../_privateInstance";

export const MAINTENANCE_MODE_QUERY_KEY = ["compliance", "maintenanceMode"] as const;

/** Status shape returned by GET/PATCH `/compliance/maintenance-mode`. */
export type MaintenanceModeStatus = {
  web: boolean;
  mobile: boolean;
};

export type MaintenanceModeChannel = keyof MaintenanceModeStatus;

/** Partial PATCH body — only the surfaces being changed. */
export type MaintenanceModePatch = Partial<MaintenanceModeStatus>;

function ensureSuccess(body: unknown, fallback: string): void {
  if (
    body &&
    typeof body === "object" &&
    (body as Record<string, unknown>).success === false
  ) {
    throw new Error(
      ((body as Record<string, unknown>).message as string) || fallback,
    );
  }
}

/**
 * Map BE payload onto the UI status. Missing keys default to `false` so a
 * partial or first-boot payload still renders controllable toggles.
 */
export function mapApiToMaintenanceMode(
  api: Partial<MaintenanceModeStatus> | null | undefined,
): MaintenanceModeStatus {
  return {
    web: Boolean(api?.web),
    mobile: Boolean(api?.mobile),
  };
}

function extractData(body: unknown): Partial<MaintenanceModeStatus> | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  if (root.data && typeof root.data === "object") {
    return root.data as Partial<MaintenanceModeStatus>;
  }
  return root as Partial<MaintenanceModeStatus>;
}

export async function fetchMaintenanceMode(): Promise<MaintenanceModeStatus> {
  const instance = getPrivateInstance();
  const { data: body } = await instance.get(API_ROUTES.GET_MAINTENANCE_MODE);
  ensureSuccess(body, AppStrings.MAINTENANCE_MODE_LOAD_ERROR);
  return mapApiToMaintenanceMode(extractData(body));
}

/**
 * PATCH only the provided surfaces. BE returns the full status after update.
 */
export async function updateMaintenanceMode(
  patch: MaintenanceModePatch,
): Promise<MaintenanceModeStatus> {
  const instance = getPrivateInstance();
  const { data: body } = await instance.patch(
    API_ROUTES.UPDATE_MAINTENANCE_MODE,
    patch,
  );
  ensureSuccess(body, AppStrings.MAINTENANCE_MODE_UPDATE_ERROR);
  return mapApiToMaintenanceMode(extractData(body));
}

export function useMaintenanceMode() {
  return useQuery({
    queryKey: MAINTENANCE_MODE_QUERY_KEY,
    queryFn: fetchMaintenanceMode,
    staleTime: 30_000,
  });
}

/** Persist a single channel toggle via partial PATCH. */
export function useUpdateMaintenanceMode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMaintenanceMode,
    onSuccess: (saved) => {
      queryClient.setQueryData(MAINTENANCE_MODE_QUERY_KEY, saved);
    },
  });
}
